import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Printer,
  FileSpreadsheet,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  TrendingDown,
  FileWarning,
  ClipboardList,
  AlertCircle,
  Loader2,
  Share2,
  Copy,
  MessageCircle,
  Repeat,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────
// Report configuration by type
// ─────────────────────────────────────────────────────────────
const REPORT_CONFIG = {
  "pending-sales": {
    title: "Pending Sales Report",
    tagline: "Trackings without a partner-submitted sale invoice",
    accent: "amber",
    icon: ClipboardList,
    columns: [
      { key: "tracking", label: "Tracking", monospace: true, width: "min-w-[140px]" },
      { key: "order_date_fmt", label: "Order Date", width: "min-w-[110px]" },
      { key: "product", label: "Product", width: "min-w-[220px]" },
      { key: "qty", label: "Qty", align: "right", width: "w-16" },
      { key: "service", label: "Service", width: "min-w-[100px]" },
      { key: "country", label: "Country", width: "min-w-[120px]" },
    ],
  },
  "negative-gp": {
    title: "Negative GP Report",
    tagline: "Parcels where Sale Amount is less than Shipment Cost (loss-making)",
    accent: "red",
    icon: TrendingDown,
    columns: [
      { key: "tracking", label: "Tracking", monospace: true, width: "min-w-[140px]" },
      { key: "date_fmt", label: "Date", width: "min-w-[110px]" },
      { key: "customer", label: "Customer", width: "min-w-[200px]" },
      { key: "purchase", label: "Purchase (₹)", align: "right", currency: true, width: "min-w-[120px]" },
      { key: "sale", label: "Sale (₹)", align: "right", currency: true, width: "min-w-[120px]" },
      { key: "gp", label: "GP (₹)", align: "right", currency: true, tone: "negative", width: "min-w-[120px]" },
      { key: "gp_pct", label: "GP %", align: "right", pct: true, tone: "negative", width: "w-24" },
    ],
    valueKey: "gp",
    valueLabel: "Total Loss",
    valueTone: "negative",
  },
  "no-invoice": {
    title: "No Invoice Report",
    tagline: "Parcels shipped but no sale invoice has been recorded yet",
    accent: "orange",
    icon: FileWarning,
    columns: [
      { key: "tracking", label: "Tracking", monospace: true, width: "min-w-[140px]" },
      { key: "date_fmt", label: "Date", width: "min-w-[110px]" },
      { key: "customer", label: "Customer", width: "min-w-[220px]" },
      { key: "purchase", label: "Purchase (₹)", align: "right", currency: true, width: "min-w-[120px]" },
      { key: "payment_mode", label: "Payment Mode", width: "min-w-[130px]" },
    ],
    valueKey: "purchase",
    valueLabel: "Total Un-invoiced Cost",
  },
  "reship-audit": {
    title: "Reship Audit Trail",
    tagline: "Every reship parcel with parser classification (Free / Paid / ESR)",
    accent: "purple",
    icon: Repeat,
    columns: [
      { key: "client_code", label: "Client", monospace: true, width: "w-24", allClientsOnly: true },
      { key: "tracking", label: "Tracking", monospace: true, width: "min-w-[140px]" },
      { key: "date_fmt", label: "Date", width: "min-w-[110px]" },
      { key: "customer", label: "Customer", width: "min-w-[180px]" },
      { key: "classification", label: "Type", width: "w-24", badge: true },
      { key: "amount", label: "Amount (₹)", align: "right", currency: true, width: "min-w-[110px]" },
      { key: "payment_mode", label: "Mode", width: "min-w-[130px]" },
      { key: "narration", label: "Narration", width: "min-w-[220px]" },
      { key: "upload_filename", label: "Source File", width: "min-w-[180px]" },
    ],
    valueKey: "amount",
    valueLabel: "Total Paid Reships",
  },
};

const ACCENT_STYLES = {
  amber: { bar: "bg-amber-500", chip: "bg-amber-100 text-amber-800", ring: "ring-amber-200" },
  red: { bar: "bg-red-500", chip: "bg-red-100 text-red-800", ring: "ring-red-200" },
  orange: { bar: "bg-orange-500", chip: "bg-orange-100 text-orange-800", ring: "ring-orange-200" },
  purple: { bar: "bg-purple-500", chip: "bg-purple-100 text-purple-800", ring: "ring-purple-200" },
};

// Format DDMMYY / dd-mm-yy / iso to dd-mmm-yy
const formatDate = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  let d, m, y;
  if (/^\d{6}$/.test(s)) {
    d = s.slice(0, 2); m = s.slice(2, 4); y = s.slice(4, 6);
  } else if (/^\d{2}-\d{2}-\d{2}$/.test(s)) {
    [d, m, y] = s.split("-");
  } else if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    y = s.slice(2, 4); m = s.slice(5, 7); d = s.slice(8, 10);
  } else {
    return s;
  }
  const mi = parseInt(m, 10) - 1;
  if (mi < 0 || mi > 11) return s;
  return `${d}-${months[mi]}-${y}`;
};

const formatMoney = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const csvEscape = (v) => {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export default function ClientReportViewer() {
  const { reportType, clientCode } = useParams();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePhone, setSharePhone] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [clientCodeFilter, setClientCodeFilter] = useState("all");

  const config = REPORT_CONFIG[reportType];
  const accent = config ? ACCENT_STYLES[config.accent] : ACCENT_STYLES.amber;
  const dateFrom = searchParams.get("date_from") || "";
  const dateTo = searchParams.get("date_to") || "";
  const isAllClientsMode = (clientCode || "").toUpperCase() === "ALL";
  // Columns visible for the current mode (drop `allClientsOnly` cols when not in ALL mode)
  const visibleColumns = useMemo(
    () => (config?.columns || []).filter(c => !c.allClientsOnly || isAllClientsMode),
    [config, isAllClientsMode]
  );

  useEffect(() => {
    document.title = `${config?.title || "Report"} — ${clientCode || ""} — EximFlow`;
  }, [config, clientCode]);

  // ─────────────────────────────────────────────────────────────
  // Fetch report data based on reportType
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!config) {
        setErrorMsg(`Unknown report type: "${reportType}"`);
        setLoading(false);
        return;
      }
      if (!token) {
        setErrorMsg("Not authenticated — please sign in on the main app first, then reload this tab.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setErrorMsg("");
      try {
        if (reportType === "pending-sales") {
          const params = new URLSearchParams();
          if (dateFrom) params.append("date_from", dateFrom);
          if (dateTo) params.append("date_to", dateTo);
          const qs = params.toString() ? `?${params}` : "";
          const res = await fetch(`${BACKEND_URL}/api/reports/pending-sales${qs}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const partner = (data?.partners || []).find(
            (p) => (p.client_code || "").toUpperCase() === (clientCode || "").toUpperCase()
          );
          if (!cancelled) {
            setClientName(partner?.partner_name || partner?.name || clientCode);
            const orders = partner?.orders || [];
            setRows(
              orders.map((o) => ({
                tracking: o.tracking || "",
                order_date: o.order_date || "",
                order_date_fmt: formatDate(o.order_date),
                product: o.product || "",
                qty: o.qty ?? "",
                service: o.service || "",
                country: o.country || "",
              }))
            );
          }
        } else if (reportType === "reship-audit") {
          const params = new URLSearchParams();
          if (dateFrom) params.append("date_from", dateFrom);
          if (dateTo) params.append("date_to", dateTo);
          const qs = params.toString() ? `?${params}` : "";
          const res = await fetch(
            `${BACKEND_URL}/api/reports/reship-audit/${encodeURIComponent(clientCode)}${qs}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          if (!cancelled) {
            setRows(
              (data?.rows || []).map((r) => ({
                client_code: r.client_code || clientCode,
                tracking: r.tracking || "",
                date: r.date || "",
                date_fmt: formatDate(r.date),
                customer: r.customer || "",
                classification: r.classification || "Free",
                amount: Number(r.amount || 0),
                payment_mode: r.payment_mode || "",
                narration: r.narration || "",
                upload_filename: r.upload_filename || "",
              }))
            );
          }
        } else {
          // negative-gp or no-invoice → partner-ledger-account
          const res = await fetch(
            `${BACKEND_URL}/api/partner-ledger-account/${encodeURIComponent(clientCode)}?view_mode=ledger`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          if (!cancelled) {
            setClientName(data?.partner_name || clientCode);
            const filterFlag = reportType === "negative-gp" ? "has_negative_gp" : "has_no_invoice";
            const filtered = (data?.ledger_entries || []).filter((e) => e[filterFlag]);
            setRows(
              filtered.map((e) => {
                const purchase = Number(e?.parcel_summary?.total_debit || 0);
                const sale = Number(e?.sale_amount || 0);
                const gp = Number(e?.gp || 0);
                const gpPct = purchase > 0 ? (gp / purchase) * 100 : 0;
                const paymentMode =
                  e?.entries?.find((x) => x.credit > 0)?.description ||
                  e?.payment_mode ||
                  "";
                return {
                  tracking: e.tracking || "",
                  date: e.date || "",
                  date_fmt: formatDate(e.date),
                  customer: e.customer || "",
                  purchase,
                  sale,
                  gp,
                  gp_pct: gpPct,
                  payment_mode: paymentMode,
                };
              })
            );
          }
        }
      } catch (err) {
        if (!cancelled) setErrorMsg(err?.message || "Failed to load report");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [reportType, clientCode, token, dateFrom, dateTo, config]);

  // Fetch lightweight client contact (name + phone) in parallel — for Share Pack
  useEffect(() => {
    if (!token || !clientCode) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/ledgers/${encodeURIComponent(clientCode)}/contact`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const c = data?.contact || {};
        // Only overwrite name if we haven't already set it from the report payload
        setClientName((prev) => prev || c.name || clientCode);
        setClientPhone(c.phone_number || c.contact || "");
      } catch (e) {
        /* silent — contact is optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, clientCode]);

  // ─────────────────────────────────────────────────────────────
  // Search + Sort + Paginate
  // ─────────────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    let base = rows;
    // Client-code filter (only applies in ALL-clients mode)
    if (isAllClientsMode && clientCodeFilter !== "all") {
      base = base.filter((r) => (r.client_code || "").toUpperCase() === clientCodeFilter);
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) =>
      Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(q))
    );
  }, [rows, searchQuery, clientCodeFilter, isAllClientsMode]);

  // Distinct client codes seen in the current data — feeds the client-code filter
  const distinctClientCodes = useMemo(() => {
    if (!isAllClientsMode) return [];
    const codes = new Set();
    for (const r of rows) if (r.client_code) codes.add(r.client_code);
    return Array.from(codes).sort();
  }, [rows, isAllClientsMode]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const an = typeof av === "number";
      const bn = typeof bv === "number";
      if (an && bn) return sortDir === "asc" ? av - bv : bv - av;
      const as = String(av ?? "").toLowerCase();
      const bs = String(bv ?? "").toLowerCase();
      if (as < bs) return sortDir === "asc" ? -1 : 1;
      if (as > bs) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredRows, sortKey, sortDir]);

  const totalRecords = sortedRows.length;
  const totalPages = pageSize === "all" ? 1 : Math.max(1, Math.ceil(totalRecords / pageSize));
  const pageRows = useMemo(() => {
    if (pageSize === "all") return sortedRows;
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, pageSize, reportType]);

  // ─────────────────────────────────────────────────────────────
  // Summary value
  // ─────────────────────────────────────────────────────────────
  const totalValue = useMemo(() => {
    if (!config?.valueKey) return null;
    return sortedRows.reduce((s, r) => s + (Number(r[config.valueKey]) || 0), 0);
  }, [sortedRows, config]);

  const totalCost = useMemo(() => {
    if (reportType === "pending-sales") return null;
    return sortedRows.reduce((s, r) => s + (Number(r.purchase) || 0), 0);
  }, [sortedRows, reportType]);

  const totalSale = useMemo(() => {
    if (reportType !== "negative-gp") return null;
    return sortedRows.reduce((s, r) => s + (Number(r.sale) || 0), 0);
  }, [sortedRows, reportType]);

  // ─────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleExportCSV = () => {
    if (sortedRows.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    const headers = visibleColumns.map((c) => c.label);
    const dataRows = sortedRows.map((r) =>
      visibleColumns.map((c) => {
        const raw = r[c.key];
        if (c.currency) return typeof raw === "number" ? raw.toFixed(2) : raw ?? "";
        if (c.pct) return typeof raw === "number" ? raw.toFixed(2) : raw ?? "";
        return raw ?? "";
      })
    );
    const csv = [headers, ...dataRows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `${reportType}_${clientCode}_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  const handlePrint = () => window.print();

  // ─────────────────────────────────────────────────────────────
  // Share Pack — WhatsApp / clipboard hand-off
  // ─────────────────────────────────────────────────────────────
  const buildShareMessage = () => {
    const nameLine = clientName && clientName !== clientCode
      ? `Hi ${clientName} (${clientCode}),`
      : `Hi ${clientCode},`;
    const count = sortedRows.length;
    const fmtIn = (n) => (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
    if (reportType === "negative-gp") {
      const loss = fmtIn(Math.abs(sortedRows.reduce((s, r) => s + (Number(r.gp) || 0), 0)));
      const cost = fmtIn(totalCost || 0);
      return (
`${nameLine}

Attached is a Negative GP report showing ${count} parcel${count === 1 ? "" : "s"} where the sale amount is below shipment cost.

• Total cost: ₹${cost}
• Total loss: ₹${loss}

Please review the CSV and share updated sale invoices so we can reconcile your ledger.

Thanks,
EximFlow Team`
      );
    }
    if (reportType === "no-invoice") {
      const cost = fmtIn(totalCost || 0);
      return (
`${nameLine}

We're missing sale invoices for ${count} shipped parcel${count === 1 ? "" : "s"} on your account.

• Total cost: ₹${cost}

Kindly share the sale details for the trackings in the attached CSV so we can update your ledger.

Thanks,
EximFlow Team`
      );
    }
    if (reportType === "reship-audit") {
      const paid = sortedRows.filter((r) => r.classification === "Paid").length;
      const free = count - paid;
      const paidSum = fmtIn(sortedRows.filter(r => r.classification === "Paid").reduce((s,r) => s + (Number(r.amount)||0), 0));
      return (
`${nameLine}

Attached is the Reship Audit for your account covering ${count} parcel${count === 1 ? "" : "s"}:

• Free reships: ${free}
• Paid reships: ${paid} (total ₹${paidSum})

Please review the CSV — it shows how each reship was classified so you can flag any misclassifications.

Thanks,
EximFlow Team`
      );
    }
    // pending-sales
    return (
`${nameLine}

We have ${count} pending parcel${count === 1 ? "" : "s"} awaiting your sale confirmation.

Please review the attached CSV and share the sale amounts / invoice numbers at your earliest.

Thanks,
EximFlow Team`
    );
  };

  const openSharePack = () => {
    if (sortedRows.length === 0) {
      toast.error("Nothing to share — the report is empty");
      return;
    }
    setSharePhone(clientPhone || "");
    setShareMessage(buildShareMessage());
    setShareOpen(true);
  };

  const cleanPhone = (raw) => String(raw || "").replace(/[^\d+]/g, "").replace(/^\+/, "");

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage);
      toast.success("Message copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please select and copy manually");
    }
  };

  const handleOpenWhatsApp = () => {
    const phone = cleanPhone(sharePhone);
    // Auto-download CSV so the user can attach it in the WhatsApp chat
    handleExportCSV();
    const text = encodeURIComponent(shareMessage);
    const url = phone
      ? `https://wa.me/${phone}?text=${text}`
      : `https://wa.me/?text=${text}`;
    // Use an anchor click instead of window.open — some Chromium contexts return
    // null from window.open('_blank', 'noopener') even on success, causing a
    // false "Pop-up blocked" toast.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setShareOpen(false);
  };

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  if (!config) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-sm border border-red-200 max-w-md p-6 text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-slate-800 mb-1">Unknown report type</h1>
          <p className="text-sm text-slate-500">
            &quot;{reportType}&quot; is not a supported report. Expected one of: pending-sales,
            negative-gp, no-invoice.
          </p>
        </div>
      </div>
    );
  }

  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-container { padding: 0 !important; }
          .print-card { box-shadow: none !important; border: 1px solid #e2e8f0 !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
        }
      `}</style>

      <div className="max-w-7xl mx-auto p-4 sm:p-6 print-container">
        {/* Header */}
        <div className={`bg-white rounded-xl shadow-sm border border-slate-200 mb-4 overflow-hidden print-card`} data-testid="report-header">
          <div className={`h-1.5 ${accent.bar}`} />
          <div className="p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className={`p-2.5 rounded-lg ${accent.chip}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight" data-testid="report-title">
                    {config.title}
                  </h1>
                  <p className="text-sm text-slate-500 mt-0.5">{config.tagline}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <Badge variant="outline" className="font-mono text-xs" data-testid="report-client-code">
                      {clientCode}
                    </Badge>
                    {clientName && clientName !== clientCode && (
                      <span className="text-sm text-slate-600">{clientName}</span>
                    )}
                    {(dateFrom || dateTo) && (
                      <Badge variant="outline" className="text-xs">
                        {dateFrom || "…"} → {dateTo || "…"}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 no-print">
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="report-print-btn">
                  <Printer className="w-4 h-4 mr-1.5" />
                  Print
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="report-csv-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-1.5" />
                  CSV
                </Button>
                <Button
                  size="sm"
                  onClick={openSharePack}
                  data-testid="report-share-pack-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Share2 className="w-4 h-4 mr-1.5" />
                  Share Pack
                </Button>
              </div>
            </div>

            {/* KPI Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
              <KPI label="Total Records" value={totalRecords.toLocaleString("en-IN")} testid="kpi-total-records" />
              {totalCost !== null && (
                <KPI label="Total Cost (₹)" value={formatMoney(totalCost)} testid="kpi-total-cost" />
              )}
              {totalSale !== null && (
                <KPI label="Total Sale (₹)" value={formatMoney(totalSale)} testid="kpi-total-sale" />
              )}
              {totalValue !== null && (
                <KPI
                  label={config.valueLabel}
                  value={`₹${formatMoney(totalValue)}`}
                  tone={config.valueTone}
                  testid="kpi-total-value"
                />
              )}
              {reportType === "pending-sales" && (
                <KPI
                  label="Search Result"
                  value={searchQuery ? `${sortedRows.length} of ${rows.length}` : "—"}
                  testid="kpi-search"
                />
              )}
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 flex items-center gap-2 flex-wrap mb-4 no-print print-card">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              className="pl-9 h-9"
              placeholder="Search tracking, customer, product…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="report-search"
            />
          </div>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(v === "all" ? "all" : parseInt(v, 10))}>
            <SelectTrigger className="w-[130px] h-9" data-testid="report-page-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25 / page</SelectItem>
              <SelectItem value="50">50 / page</SelectItem>
              <SelectItem value="100">100 / page</SelectItem>
              <SelectItem value="all">Show all</SelectItem>
            </SelectContent>
          </Select>
          {/* Client-code filter — only in ALL-clients mode */}
          {isAllClientsMode && distinctClientCodes.length > 1 && (
            <Select value={clientCodeFilter} onValueChange={setClientCodeFilter}>
              <SelectTrigger className="w-[170px] h-9" data-testid="report-client-filter">
                <SelectValue placeholder="All clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients ({distinctClientCodes.length})</SelectItem>
                {distinctClientCodes.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {sortKey && (
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-500"
              onClick={() => {
                setSortKey(null);
                setSortDir("asc");
              }}
              data-testid="report-clear-sort"
            >
              Clear sort
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden print-card">
          {loading ? (
            <div className="p-16 text-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-slate-400" />
              Loading report…
            </div>
          ) : errorMsg ? (
            <div className="p-10 text-center">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600">{errorMsg}</p>
            </div>
          ) : sortedRows.length === 0 ? (
            <div className="p-16 text-center text-slate-400 text-sm">
              No matching records
              {searchQuery && (
                <>
                  {" "}for &quot;<b>{searchQuery}</b>&quot;
                </>
              )}
              .
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 w-12">
                        #
                      </th>
                      {visibleColumns.map((c) => (
                        <th
                          key={c.key}
                          className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 cursor-pointer select-none hover:bg-slate-100 ${
                            c.align === "right" ? "text-right" : "text-left"
                          } ${c.width || ""}`}
                          onClick={() => toggleSort(c.key)}
                        >
                          <span className="inline-flex items-center gap-1">
                            {c.label}
                            {sortKey === c.key ? (
                              sortDir === "asc" ? (
                                <ArrowUp className="w-3 h-3" />
                              ) : (
                                <ArrowDown className="w-3 h-3" />
                              )
                            ) : (
                              <ArrowUpDown className="w-3 h-3 opacity-30" />
                            )}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((r, idx) => {
                      const globalIdx = pageSize === "all" ? idx + 1 : (page - 1) * pageSize + idx + 1;
                      return (
                        <tr
                          key={`${r.tracking}-${idx}`}
                          className={`border-b border-slate-100 ${idx % 2 === 1 ? "bg-slate-50/40" : ""} hover:bg-slate-50`}
                          data-testid={`report-row-${idx}`}
                        >
                          <td className="px-3 py-2 text-xs text-slate-400 font-mono">{globalIdx}</td>
                          {visibleColumns.map((c) => {
                            const raw = r[c.key];
                            let display;
                            if (c.currency) {
                              display = `₹${formatMoney(raw)}`;
                            } else if (c.pct) {
                              display = typeof raw === "number" ? `${raw.toFixed(2)}%` : "—";
                            } else if (c.badge) {
                              const cls =
                                raw === "Paid"
                                  ? "bg-emerald-100 text-emerald-800"
                                  : raw === "ESR (Free)"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-purple-100 text-purple-800";
                              display = (
                                <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>
                                  {raw || "Free"}
                                </span>
                              );
                            } else {
                              display = raw || "—";
                            }
                            const isNeg = c.tone === "negative" && typeof raw === "number" && raw < 0;
                            return (
                              <td
                                key={c.key}
                                className={`px-3 py-2 ${
                                  c.align === "right" ? "text-right tabular-nums" : ""
                                } ${c.monospace ? "font-mono text-xs" : ""} ${
                                  isNeg ? "text-red-600 font-semibold" : "text-slate-700"
                                }`}
                              >
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pageSize !== "all" && totalPages > 1 && (
                <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-slate-50 no-print">
                  <div className="text-xs text-slate-500">
                    Showing{" "}
                    <b>
                      {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalRecords)}
                    </b>{" "}
                    of <b>{totalRecords}</b>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2"
                      disabled={page <= 1}
                      onClick={() => setPage(1)}
                      data-testid="report-page-first"
                    >
                      <ChevronsLeft className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      data-testid="report-page-prev"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </Button>
                    <span className="text-xs px-2 text-slate-600 tabular-nums">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      data-testid="report-page-next"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2"
                      disabled={page >= totalPages}
                      onClick={() => setPage(totalPages)}
                      data-testid="report-page-last"
                    >
                      <ChevronsRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-xs text-slate-400 text-center mt-4">
          Generated {new Date().toLocaleString("en-IN")} · EximFlow
        </p>
      </div>

      {/* ─── Share Pack Dialog ───────────────────────────────────── */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="share-pack-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-emerald-600" />
              Share Pack — {config?.title}
            </DialogTitle>
            <DialogDescription>
              Send this report to <b>{clientName || clientCode}</b> in one tap. The CSV
              downloads automatically when you open WhatsApp — attach it in the chat.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="share-phone" className="text-xs font-medium text-slate-600">
                WhatsApp number (with country code — e.g., 91XXXXXXXXXX)
              </Label>
              <Input
                id="share-phone"
                value={sharePhone}
                onChange={(e) => setSharePhone(e.target.value)}
                placeholder="e.g., 919876543210"
                data-testid="share-pack-phone"
                className="mt-1 font-mono"
              />
              {!clientPhone && (
                <p className="text-[11px] text-amber-600 mt-1">
                  No phone number saved for this client — enter it manually or leave blank
                  to pick a contact in WhatsApp.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="share-message" className="text-xs font-medium text-slate-600">
                Message (editable)
              </Label>
              <Textarea
                id="share-message"
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                rows={9}
                data-testid="share-pack-message"
                className="mt-1 font-mono text-xs leading-relaxed"
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-[11px] text-slate-400">
                  {shareMessage.length} chars · {sortedRows.length} records will be listed in the CSV
                </span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2 flex-row justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyMessage}
              data-testid="share-pack-copy"
            >
              <Copy className="w-4 h-4 mr-1.5" />
              Copy message
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              data-testid="share-pack-csv"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />
              CSV only
            </Button>
            <Button
              size="sm"
              onClick={handleOpenWhatsApp}
              data-testid="share-pack-whatsapp"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <MessageCircle className="w-4 h-4 mr-1.5" />
              Open in WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const KPI = ({ label, value, tone, testid }) => (
  <div
    className="border border-slate-200 rounded-lg px-3 py-2 bg-slate-50/60"
    data-testid={testid}
  >
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div
      className={`text-lg font-bold tabular-nums ${
        tone === "negative" ? "text-red-600" : "text-slate-900"
      }`}
    >
      {value}
    </div>
  </div>
);
